import React, {
  lazy,
  Suspense,
  useEffect,
  useState,
} from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";

import "./App.css";

import ScrollToTop from "./pages/ScrollToTop";

/* ============================================================
   LAZY LOADED PUBLIC PAGES
============================================================ */

const HomePage = lazy(() =>
  import("./pages/HomePage")
);

const AboutFull = lazy(() =>
  import("./components/AboutFull")
);

const WhyusFull = lazy(() =>
  import("./components/WhyusFull")
);

const CoursesFull = lazy(() =>
  import("./components/CoursesFull")
);

const CourseDetail = lazy(() =>
  import("./components/CourseDetail")
);

const EnrollForm = lazy(() =>
  import("./pages/EnrollForm")
);

const DemoSession = lazy(() =>
  import("./components/DemoSession")
);

const Consultation = lazy(() =>
  import("./components/Consultations")
);

const Login = lazy(() =>
  import("./pages/Login")
);

const Blog = lazy(() =>
  import("./pages/Blog")
);

const BlogDetails = lazy(() =>
  import("./pages/BlogDetails")
);

const Contact = lazy(() =>
  import("./pages/Contact")
);

const PrivacyPolicy = lazy(() =>
  import("./pages/PrivacyPolicy")
);

const TermsConditions = lazy(() =>
  import("./pages/TermsConditions")
);

const Disclaimer = lazy(() =>
  import("./pages/Disclaimer")
);

const RefundPolicy = lazy(() =>
  import("./pages/RefundPolicy")
);

/* ============================================================
   LIVE DEMO POPUP
============================================================ */

const LiveDemoPopup = lazy(() =>
  import("./components/LiveDemoPopup")
);

/* ============================================================
   ADMIN LAYOUT
============================================================ */

const AdminLayout = lazy(() =>
  import("./components/AdminLayout")
);

/* ============================================================
   ADMIN PAGES
============================================================ */

const ManageCourses = lazy(() =>
  import("./pages/ManageCourses")
);

const Enrollments = lazy(() =>
  import("./pages/Enrollments")
);

const HomeSettings = lazy(() =>
  import("./pages/HomeSettings")
);

const AdminAddBlog = lazy(() =>
  import("./pages/AdminAddBlog")
);

const EditBlog = lazy(() =>
  import("./pages/EditBlog")
);

const AdminAbout = lazy(() =>
  import("./pages/AdminAbout")
);

const AddWhyUs = lazy(() =>
  import("./pages/AddWhyUs")
);

const AddWhoWeServe = lazy(() =>
  import("./pages/AddWhoWeServe")
);

const AdminTestimonials = lazy(() =>
  import("./pages/AdminTestimonials")
);

const CourseFeaturesAdmin = lazy(() =>
  import("./pages/AdminCourseSidebarFeatures")
);

const AdminLoginUsers = lazy(() =>
  import("./pages/AdminLoginUsers")
);

const AdminDemoSessions = lazy(() =>
  import("./pages/AdminDemoSessions")
);

const AdminConsultations = lazy(() =>
  import("./pages/AdminConsultations")
);

/* ============================================================
   PAGE LOADER
============================================================ */

function PageLoader() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "16px",
      }}
    >
      Loading...
    </div>
  );
}

/* ============================================================
   APP
============================================================ */

function App() {
  const [showPopup, setShowPopup] = useState(false);

  /* ==========================================================
     LOAD POPUP ONLY AFTER INITIAL PAGE LOAD

     The original code displayed the popup after 2.5 seconds.
     This can interfere with Lighthouse/PageSpeed measurements.

     We wait until the page has loaded and the browser gets
     idle time before loading the popup.
  ========================================================== */

  useEffect(() => {
    let timeoutId;
    let idleId;

    const schedulePopup = () => {
      timeoutId = setTimeout(() => {
        if ("requestIdleCallback" in window) {
          idleId = window.requestIdleCallback(
            () => {
              setShowPopup(true);
            },
            {
              timeout: 3000,
            },
          );
        } else {
          setShowPopup(true);
        }
      }, 5000);
    };

    if (document.readyState === "complete") {
      schedulePopup();
    } else {
      window.addEventListener(
        "load",
        schedulePopup,
        {
          once: true,
        },
      );
    }

    return () => {
      clearTimeout(timeoutId);

      if (
        idleId &&
        "cancelIdleCallback" in window
      ) {
        window.cancelIdleCallback(idleId);
      }

      window.removeEventListener(
        "load",
        schedulePopup,
      );
    };
  }, []);

  return (
    <Router basename="/">
      <ScrollToTop />

      {/* ========================================================
          LIVE DEMO POPUP

          Separate Suspense boundary ensures that loading the
          popup does not affect the main website.
      ======================================================== */}

      {showPopup && (
        <Suspense fallback={null}>
          <LiveDemoPopup />
        </Suspense>
      )}

      {/* ========================================================
          ROUTES
      ======================================================== */}

      <Suspense fallback={<PageLoader />}>
        <Routes>

          {/* ====================================================
              HOME PAGE
          ==================================================== */}

          <Route
            path="/"
            element={<HomePage />}
          />

          <Route
            path="/about"
            element={<HomePage />}
          />

          <Route
            path="/whyus"
            element={<HomePage />}
          />

          <Route
            path="/testimonials"
            element={<HomePage />}
          />

          <Route
            path="/training"
            element={<HomePage />}
          />

          <Route
            path="/ourcourses"
            element={<HomePage />}
          />

          {/* ====================================================
              ABOUT
          ==================================================== */}

          <Route
            path="/about-us"
            element={<AboutFull />}
          />

          <Route
            path="/why-swiftintelli"
            element={<WhyusFull />}
          />

          {/* ====================================================
              COURSES
          ==================================================== */}

          <Route
            path="/courses"
            element={<CoursesFull />}
          />

          <Route
            path="/courses/:slug"
            element={<CourseDetail />}
          />

          {/* ====================================================
              FREE DEMO
          ==================================================== */}

          <Route
            path="/join-free-demo/:slug"
            element={<DemoSession />}
          />

          {/* ====================================================
              CONSULTATION
          ==================================================== */}

          <Route
            path="/consultation"
            element={<Consultation />}
          />

          {/* ====================================================
              ENROLLMENT
          ==================================================== */}

          <Route
            path="/enroll/:slug"
            element={<EnrollForm />}
          />

          {/* ====================================================
              LOGIN
          ==================================================== */}

          <Route
            path="/login"
            element={<Login />}
          />

          {/* ====================================================
              BLOGS
          ==================================================== */}

          <Route
            path="/blogs"
            element={<Blog />}
          />

          <Route
            path="/blogs/:slug"
            element={<BlogDetails />}
          />

          {/* ====================================================
              CONTACT
          ==================================================== */}

          <Route
            path="/contact-us"
            element={<Contact />}
          />

          {/* ====================================================
              LEGAL PAGES
          ==================================================== */}

          <Route
            path="/privacy-policy"
            element={<PrivacyPolicy />}
          />

          <Route
            path="/terms-and-conditions"
            element={<TermsConditions />}
          />

          <Route
            path="/disclaimer"
            element={<Disclaimer />}
          />

          <Route
            path="/refund-cancellation-policy"
            element={<RefundPolicy />}
          />

          {/* ====================================================
              ADMIN SECTION
          ==================================================== */}

          <Route element={<AdminLayout />}>

            {/* HOME SETTINGS */}

            <Route
              path="/home-settings"
              element={<HomeSettings />}
            />

            {/* COURSES */}

            <Route
              path="/manage-courses"
              element={<ManageCourses />}
            />

            <Route
              path="/admin-course-features"
              element={<CourseFeaturesAdmin />}
            />

            {/* ENROLLMENTS */}

            <Route
              path="/enrollments"
              element={<Enrollments />}
            />

            {/* BLOG MANAGEMENT */}

            <Route
              path="/admin-add-blog"
              element={<AdminAddBlog />}
            />

            <Route
              path="/edit-blog/:id"
              element={<EditBlog />}
            />

            {/* ABOUT MANAGEMENT */}

            <Route
              path="/admin-about"
              element={<AdminAbout />}
            />

            <Route
              path="/admin-whyus"
              element={<AddWhyUs />}
            />

            <Route
              path="/admin-who-we-serve"
              element={<AddWhoWeServe />}
            />

            <Route
              path="/admin-testimonials"
              element={<AdminTestimonials />}
            />

            {/* DEMO SESSIONS */}

            <Route
              path="/admin-demo-sessions"
              element={<AdminDemoSessions />}
            />

            {/* CONSULTATIONS */}

            <Route
              path="/admin-consultations"
              element={<AdminConsultations />}
            />

            {/* USERS */}

            <Route
              path="/login-users"
              element={<AdminLoginUsers />}
            />

          </Route>

          {/* ====================================================
              404 PAGE
          ==================================================== */}

          <Route
            path="*"
            element={<NotFound />}
          />

        </Routes>
      </Suspense>
    </Router>
  );
}

/* ============================================================
   404 COMPONENT
============================================================ */

function NotFound() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "20px",
      }}
    >
      <h1
        style={{
          fontSize: "42px",
          marginBottom: "10px",
        }}
      >
        Page Not Found
      </h1>

      <p
        style={{
          fontSize: "18px",
          color: "#64748b",
        }}
      >
        The page you are looking for does not exist.
      </p>
    </div>
  );
}

export default App;